import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useStudentTeacherHistory } from "@/hooks/useStudentTeacherHistory";
import { useStudentDepletionDetail } from "@/hooks/useStudentDepletionDetail";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, parseISO } from "date-fns";
import { th } from "date-fns/locale";
import { Users, CreditCard, Calendar, Clock, CalendarDays } from "lucide-react";

interface StudentTeacherHistoryModalProps {
  studentCode: string | null;
  currentTeacherCode?: string;
  onClose: () => void;
}

export const StudentTeacherHistoryModal: React.FC<StudentTeacherHistoryModalProps> = ({
  studentCode,
  currentTeacherCode,
  onClose,
}) => {
  const { data, isLoading, error } = useStudentTeacherHistory(studentCode);
  const { classTemplates, isLoading: isLoadingSchedule } = useStudentDepletionDetail(studentCode);

  const { data: studentInfo } = useQuery({
    queryKey: ["student-info", studentCode],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .schema("student")
        .from("fishsix_students")
        .select("hours, weekly_scheduled_hours, estimated_hours_depletion_date")
        .eq("code", studentCode!)
        .single();
      return data;
    },
    enabled: !!studentCode,
  });


  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    try {
      return format(parseISO(dateStr), "d MMM yyyy", { locale: th });
    } catch {
      return dateStr;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('th-TH').format(amount);
  };

  const formatTime = (time: string | null) => {
    if (!time) return "-";
    return time.substring(0, 5); // HH:MM
  };

  return (
    <Dialog open={!!studentCode} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            ข้อมูลนักเรียน {studentCode}
            {data?.student_nickname && (
              <span className="text-muted-foreground font-normal">
                ({data.student_nickname})
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Student Summary Cards */}
        {studentInfo && (
          <div className="grid grid-cols-3 gap-3">
            <div className="flex items-center gap-2 rounded-lg border bg-muted/50 p-3">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">ชม. คงเหลือ</p>
                <p className="text-sm font-semibold">
                  {studentInfo.hours != null ? `${studentInfo.hours} ชม.` : "-"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg border bg-muted/50 p-3">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">เรียน/สัปดาห์</p>
                <p className="text-sm font-semibold">
                  {studentInfo.weekly_scheduled_hours
                    ? `${studentInfo.weekly_scheduled_hours} ชม.`
                    : "ไม่มีตารางประจำ"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 rounded-lg border bg-muted/50 p-3">
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="text-xs text-muted-foreground">คาดว่าจะหมด</p>
                <p className="text-sm font-semibold">
                  {formatDate(studentInfo.estimated_hours_depletion_date)}
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="flex-1 overflow-hidden flex flex-col">
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center text-destructive py-8">
              เกิดข้อผิดพลาดในการโหลดข้อมูล
            </div>
          ) : (
            <Tabs defaultValue="teachers" className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="teachers" className="gap-2">
                  <Users className="h-4 w-4" />
                  ประวัติครู ({data?.teachers.length || 0})
                </TabsTrigger>
                <TabsTrigger value="purchases" className="gap-2">
                  <CreditCard className="h-4 w-4" />
                  การซื้อคอร์ส ({data?.purchases.length || 0})
                </TabsTrigger>
                <TabsTrigger value="schedule" className="gap-2">
                  <Calendar className="h-4 w-4" />
                  ตารางประจำ ({classTemplates.length})
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="teachers" className="flex-1 overflow-auto mt-4">
                {!data?.teachers.length ? (
                  <div className="text-center text-muted-foreground py-8">
                    ไม่พบประวัติการเรียน
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>รหัสครู</TableHead>
                        <TableHead>ชื่อครู</TableHead>
                        <TableHead className="text-right">คลาส</TableHead>
                        <TableHead className="text-right">ชั่วโมง</TableHead>
                        <TableHead className="text-right">เรียนล่าสุด</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.teachers.map((teacher) => (
                        <TableRow 
                          key={teacher.teacher_code}
                          className={teacher.teacher_code === currentTeacherCode 
                            ? "bg-blue-100 dark:bg-blue-900/50 ring-2 ring-primary ring-inset" 
                            : ""
                          }
                        >
                          <TableCell className="font-mono text-sm">
                            {teacher.teacher_code}
                          </TableCell>
                          <TableCell className="font-medium">
                            {teacher.teacher_name || "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            {teacher.total_classes}
                          </TableCell>
                          <TableCell className="text-right">
                            {teacher.total_hours.toFixed(1)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatDate(teacher.last_class_date)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
              
              <TabsContent value="purchases" className="flex-1 overflow-auto mt-4">
                {!data?.purchases.length ? (
                  <div className="text-center text-muted-foreground py-8">
                    ไม่พบประวัติการซื้อคอร์ส
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>วันที่จ่าย</TableHead>
                        <TableHead className="text-right">จำนวนเงิน</TableHead>
                        <TableHead>ประเภท</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.purchases.map((purchase, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            {formatDate(purchase.payment_date)}
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(purchase.amount)}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={purchase.type === 'new' ? 'default' : 'secondary'}
                              className={purchase.type === 'new' 
                                ? 'bg-green-500 hover:bg-green-600' 
                                : ''
                              }
                            >
                              {purchase.type === 'new' ? 'ยอดใหม่' : 'ต่อคอร์ส'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
              
              <TabsContent value="schedule" className="flex-1 overflow-auto mt-4">
                {isLoadingSchedule ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : !classTemplates.length ? (
                  <div className="text-center text-muted-foreground py-8">
                    ไม่มีตารางประจำ
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>วัน</TableHead>
                        <TableHead>เวลา</TableHead>
                        <TableHead>วิชา</TableHead>
                        <TableHead>สถานที่</TableHead>
                        <TableHead>รหัสครู</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {classTemplates.map((ct) => (
                        <TableRow key={ct.id}>
                          <TableCell className="font-medium">{ct.weekday_name}</TableCell>
                          <TableCell>{formatTime(ct.start_time)} - {formatTime(ct.end_time)}</TableCell>
                          <TableCell>{ct.subject || "-"}</TableCell>
                          <TableCell>{ct.location || "-"}</TableCell>
                          <TableCell className="font-mono text-sm">{ct.teacher_code || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            </Tabs>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
